import { FunctionComponent, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Drawer,
    IconButton,
    Stack,
    SvgIcon,
    Typography,
    useTheme,
} from '@mui/material';
import { IntroText } from '@/pageContents/specialTarifs/IntroText';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useGetCardCodesQuery } from '@/api/v2/cardCodes';
import { StarjackTable } from '@/components/Table/v2/Table';
import { usePagination } from '@/hooks/usePagination';
import { CardCode } from '@/models/cardcodes';
import Details from '@heroicons/react/24/solid/DocumentMagnifyingGlassIcon';
import Link from '@mui/material/Link';
import { NewSpecialTarifCard } from '@/pageContents/specialTarifs/NewSpecialTarifCard';
import { useNavigate } from 'react-router-dom';
import { useSlice } from '@/hooks/useSlice';
import { pageDataSlice } from '@starjack/features/pageData';

type SpecialTarifsContentProps = {
    isSpecialTarifsNew: boolean;
};
export const SpecialTarifsContent: FunctionComponent<
    SpecialTarifsContentProps
> = ({ isSpecialTarifsNew }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();

    const { data, isLoading } = useGetCardCodesQuery({ liftOperatorId });
    const { currentItems, setNextPage, itemsPerPage, setItemsPerPage } =
        usePagination({
            items: data,
            itemsPerPage: 10,
        });
    const theme = useTheme();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const {
        actions: { setPageData },
    } = useSlice(pageDataSlice, 'requestSlice');

    const toggleSpecialTarifsNewDrawer = () => {
        if (isSpecialTarifsNew) {
            setIsOpen(false);

            setTimeout(() => {
                navigate('/spezialtarife');
            }, 100);
        } else {
            navigate('/spezialtarife/neu');
        }
    };

    useEffect(() => {
        if (isSpecialTarifsNew) {
            setTimeout(() => {
                setIsOpen(true);
            }, 100);
        }

        setPageData({
            title: 'Spezialtarife',
            body: <IntroText />,
        });
    }, []);

    return (
        <>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Spezialtarife
            </Typography>

            <Stack spacing={theme.page.verticalSpacing}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        width: '100%',
                        p: 3,
                    }}
                >
                    <Button
                        onClick={toggleSpecialTarifsNewDrawer}
                        variant='contained'
                    >
                        + Neuen SpezialTarif anlegen
                    </Button>
                </Box>

                <Drawer
                    anchor='right'
                    open={isOpen}
                    onClose={toggleSpecialTarifsNewDrawer}
                >
                    <NewSpecialTarifCard
                        onClose={toggleSpecialTarifsNewDrawer}
                    />
                </Drawer>

                <StarjackTable<CardCode>
                    count={data?.length}
                    items={currentItems}
                    columns={[
                        {
                            label: 'Name der Freigabegruppe',
                            itemKey: 'externalCode',
                        },
                        {
                            label: 'Anzahl der Freigabe-Codes',
                            itemTransform: (item) => item.singleCodes.length,
                            options: {
                                headerCellProps: {
                                    align: 'right',
                                },
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                        },
                        {
                            label: 'Details / Bearbeiten',
                            itemTransform: (item) => (
                                <Link
                                    href={`/spezialtarife/details/?id=${item.id}`}
                                >
                                    <IconButton>
                                        <SvgIcon>
                                            <Details />
                                        </SvgIcon>
                                    </IconButton>
                                </Link>
                            ),
                            options: {
                                headerCellProps: {
                                    align: 'right',
                                },
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                        },
                    ]}
                    itemId={'id'}
                    loading={isLoading}
                    onRowsPerPageChange={setItemsPerPage}
                    rowsPerPage={itemsPerPage}
                    onPageChange={setNextPage}
                />
            </Stack>
        </>
    );
};
